// Search bar with grouped typeahead for the CPLEX Sankey page.
//
// Reads from the prebuilt search index, ranks entries against the input
// query, and shows a grouped dropdown. Selecting an entry calls
// `onAddFilter` so the parent can append it to the chip list.

import { useEffect, useMemo, useRef, useState } from 'react';
// safeActiveIdx is clamped to [0, flat.length-1] on use so it can never index
// past the end after `query` changes shrink the result set.
import styles from './SankeySearchBar.module.css';
import {
  KIND_LABELS,
  buildQueryChip,
  type EntityKind,
  type SearchEntity,
} from './searchIndex';
import { rankEntities } from './searchRank';

interface SankeySearchBarProps {
  entities: readonly SearchEntity[];
  onAddFilter: (entity: SearchEntity) => void;
  // Chips already applied — used to grey-out / disable duplicates.
  applied: readonly SearchEntity[];
  placeholder?: string;
}

function chipKey(e: SearchEntity): string {
  return `${e.kind}::${e.value}`;
}

export function SankeySearchBar({
  entities,
  onAddFilter,
  applied,
  placeholder = 'Search customers, orders, RMs, vendors…',
}: SankeySearchBarProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  const appliedKeys = useMemo(
    () => new Set(applied.map(chipKey)),
    [applied],
  );

  const groups = useMemo(() => rankEntities(entities, query), [entities, query]);

  // Synthetic "Includes: <query>" pseudo-option. Built whenever the query
  // matches at least one entity, even when an exact specific match is also
  // surfaced — so the user can always commit a free-text chip instead of
  // narrowing to one specific row.
  const includeAllChip = useMemo(
    () => buildQueryChip(query, entities),
    [query, entities],
  );

  // Flatten the include-all pseudo-row + ranked groups so up/down keys can
  // navigate the whole list. The include-all entry sits at index 0 when
  // present so Enter without arrow-navigation commits the "contains" chip.
  const flat = useMemo(() => {
    const out: { kind: EntityKind; entity: SearchEntity }[] = [];
    if (includeAllChip) {
      out.push({ kind: 'query', entity: includeAllChip });
    }
    for (const g of groups) {
      for (const r of g.results) out.push({ kind: g.kind, entity: r.entity });
    }
    return out;
  }, [includeAllChip, groups]);

  // Clamp the stored active index against the current flat list so the value
  // is always valid without needing an effect to reset it on every query
  // change (which would cascade renders and trip the no-set-state-in-effect
  // lint).
  const safeActiveIdx =
    flat.length === 0 ? 0 : Math.min(activeIdx, flat.length - 1);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const commit = (e: SearchEntity) => {
    if (!appliedKeys.has(chipKey(e))) onAddFilter(e);
    setQuery('');
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || flat.length === 0) {
      if (e.key === 'Escape') setOpen(false);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, flat.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const pick = flat[safeActiveIdx];
      if (pick) commit(pick.entity);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const showDropdown = open && query.trim().length > 0;

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <div className={styles.inputRow}>
        <span className={styles.icon} aria-hidden="true">⌕</span>
        <input
          type="search"
          className={styles.input}
          value={query}
          placeholder={placeholder}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          aria-label="Search the Sankey dashboard"
          aria-autocomplete="list"
          aria-expanded={showDropdown}
        />
      </div>
      {showDropdown && (
        <div className={styles.dropdown} role="listbox">
          {flat.length === 0 ? (
            <div className={styles.empty}>No matches in this run.</div>
          ) : (
            <>
              {includeAllChip && (
                <div className={styles.group}>
                  <div className={styles.groupHeader}>Quick filter</div>
                  {(() => {
                    const active = safeActiveIdx === 0;
                    const disabled = appliedKeys.has(chipKey(includeAllChip));
                    return (
                      <div
                        role="option"
                        aria-selected={active}
                        className={`${styles.option} ${styles.queryOption} ${
                          active ? styles.optionActive : ''
                        }`}
                        style={{
                          opacity: disabled ? 0.4 : 1,
                          cursor: disabled ? 'default' : 'pointer',
                        }}
                        onMouseEnter={() => setActiveIdx(0)}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          if (!disabled) commit(includeAllChip);
                        }}
                      >
                        <span>
                          <span className={styles.queryPrefix}>includes:</span>{' '}
                          {includeAllChip.label}
                        </span>
                        <span className={styles.count}>
                          {includeAllChip.rowIndices.length} flows
                        </span>
                      </div>
                    );
                  })()}
                </div>
              )}
              {groups.map((g) => {
                const firstEntity = g.results[0]?.entity;
                const startIdx = flat.findIndex(
                  (f) => f.kind === g.kind && f.entity === firstEntity,
                );
                return (
                  <div key={g.kind} className={styles.group}>
                    <div className={styles.groupHeader}>
                      {KIND_LABELS[g.kind]}
                    </div>
                    {g.results.map((r, i) => {
                      const idx = startIdx + i;
                      const active = idx === safeActiveIdx;
                      const disabled = appliedKeys.has(chipKey(r.entity));
                      return (
                        <div
                          key={chipKey(r.entity)}
                          role="option"
                          aria-selected={active}
                          className={`${styles.option} ${
                            active ? styles.optionActive : ''
                          }`}
                          style={{
                            opacity: disabled ? 0.4 : 1,
                            cursor: disabled ? 'default' : 'pointer',
                          }}
                          onMouseEnter={() => setActiveIdx(idx)}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            if (!disabled) commit(r.entity);
                          }}
                        >
                          <span>{r.entity.label}</span>
                          <span className={styles.count}>
                            {r.entity.rowIndices.length} flows
                          </span>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}
